// Stage 47: Phantom Module — Settings tab (full inspector).
//
// Operator-only growth-direction harness. Mirrors HomunculusTab.tsx /
// MemoryAdvTab.tsx in style. Renders:
//   - Enable switch + short explainer.
//   - Last-regeneration banner with [Regenerate now] [Export NDJSON].
//   - Stub list with type filter + per-stub [Copy] [Suppress] [Details].
//   - Suppression panel with [Restore].
//   - History modal-ish list.
//   - Horizon-doc uploader (operator + bundled, separately).
//   - Detector threshold tuners (debounced PUT /api/settings).
//   - Polish toggle.
//
// NO Gemini-tool side-effects. The model never sees this surface.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { apiGet, apiJson } from '../api/client';
import {
  phantomHealthcheck,
  phantomRegenerate,
  phantomReadJson,
  phantomReadMarkdown,
  phantomSuppress,
  phantomUnsuppress,
  phantomListSuppressions,
  phantomHistory,
  phantomExportUrl,
  phantomListHorizon,
  phantomUploadHorizon,
  phantomDeleteHorizon,
  type PhantomHealth,
  type PhantomStub,
  type PhantomRegenSummary,
  type PhantomSuppression,
  type PhantomHistoryItem,
  type PhantomHorizonOperatorDoc,
  type PhantomHorizonBundledDoc,
  type StubType,
  type DetectorType,
} from '../api/phantom';

interface SettingsResponse {
  settings: Record<string, string>;
}

const API_BASE = import.meta.env.VITE_API_BASE as string;

type TypeFilter = 'all' | StubType;

const BADGE: Record<StubType, { bg: string; fg: string; label: string }> = {
  'capability-gap':         { bg: '#fef3c7', fg: '#92400e', label: 'Capability' },
  'redundancy':             { bg: '#e0f2fe', fg: '#075985', label: 'Redundancy' },
  'calibration-drift':      { bg: '#ffe4e6', fg: '#9f1239', label: 'Drift' },
  'architectural-pressure': { bg: '#d1fae5', fg: '#065f46', label: 'Architectural' },
};

const THRESHOLD_KEYS = [
  ['phantom_recurrent_recall_min_depth', 'recurrent-recall min supersession depth', '3'],
  ['phantom_recurrent_recall_min_sessions', 'recurrent-recall min sessions', '2'],
  ['phantom_closeout_zone_min_hits', 'closeout-zone min hits', '3'],
  ['phantom_calibration_drift_window', 'calibration drift window (sessions)', '10'],
  ['phantom_calibration_drift_min_sessions', 'calibration drift min sessions', '5'],
  ['phantom_calibration_drift_slope', 'calibration drift slope (negative)', '-0.02'],
  ['phantom_bias_persistence_window', 'bias persistence window', '8'],
  ['phantom_bias_persistence_min_hits', 'bias persistence min hits', '3'],
  ['phantom_horizon_jaccard', 'horizon Jaccard threshold', '0.4'],
  ['phantom_wipeout_repetition_min_hits', 'wipeout-repetition min hits', '2'],
  ['phantom_max_stubs', 'max stubs in Phantom.md', '50'],
  ['phantom_freq_normalization_target', 'frequency normalisation target', '10'],
  ['phantom_recency_halflife_days', 'recency halflife (days)', '30'],
] as const;

function fmtAgo(ts: number): string {
  if (!ts) return 'never';
  const diff = Math.floor(Date.now() / 1000) - ts;
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function fmtScore(n: number): string {
  return n.toFixed(2);
}

function copyToClipboard(text: string): Promise<boolean> {
  if (navigator.clipboard?.writeText) {
    return navigator.clipboard.writeText(text).then(() => true).catch(() => false);
  }
  return Promise.resolve(false);
}

function stubToClipboardText(s: PhantomStub, rank: number): string {
  const evidence = s.evidence
    .map((e) => `- ${e.source}: ${e.detector} (×${e.signalCount})`)
    .join('\n');
  return [
    `[PHANTOM STUB #${String(rank).padStart(3, '0')}]`,
    `Title: ${s.title}`,
    `Type: ${s.type}`,
    `Value score: ${fmtScore(s.valueScore)}`,
    `First seen: ${s.firstSeenAt}`,
    '',
    'Pressure:',
    s.pressure,
    '',
    'Suggested scope:',
    s.suggestedScope,
    '',
    'Evidence:',
    evidence,
    '',
    `Stub ID: ${s.id}`,
  ].join('\n');
}

export function PhantomTab({ onChanged }: { onChanged: () => void }) {
  // master state
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [polishEnabled, setPolishEnabled] = useState(false);
  const [health, setHealth] = useState<PhantomHealth | null>(null);
  const [healthErr, setHealthErr] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  // current Phantom.md
  const [current, setCurrent] = useState<PhantomRegenSummary | null>(null);
  const [currentMd, setCurrentMd] = useState<string>('');
  const [readBusy, setReadBusy] = useState(false);
  const [regenBusy, setRegenBusy] = useState(false);

  // filtering
  const [filter, setFilter] = useState<TypeFilter>('all');
  const [minScore, setMinScore] = useState<number>(0.0);
  const [expandedStub, setExpandedStub] = useState<string | null>(null);

  // suppression panel
  const [suppressions, setSuppressions] = useState<PhantomSuppression[]>([]);
  const [suppressionsOpen, setSuppressionsOpen] = useState(false);

  // history
  const [history, setHistoryList] = useState<PhantomHistoryItem[]>([]);
  const [historyOpen, setHistoryOpen] = useState(false);

  // horizon docs
  const [opDocs, setOpDocs] = useState<PhantomHorizonOperatorDoc[]>([]);
  const [bundledDocs, setBundledDocs] = useState<PhantomHorizonBundledDoc[]>([]);
  const [horizonOpen, setHorizonOpen] = useState(false);
  const [horizonFile, setHorizonFile] = useState<{ filename: string; body: string } | null>(null);

  // thresholds
  const [thresholds, setThresholds] = useState<Record<string, string>>({});
  const [thresholdsOpen, setThresholdsOpen] = useState(false);
  const debounceRefs = useRef<Record<string, ReturnType<typeof setTimeout> | null>>({});

  // suppress modal
  const [suppressFor, setSuppressFor] = useState<PhantomStub | null>(null);
  const [suppressReason, setSuppressReason] = useState('');
  const [suppressExpiry, setSuppressExpiry] = useState('');

  const loadSettings = useCallback(async (): Promise<void> => {
    try {
      const res = await apiGet<SettingsResponse>('/api/settings');
      const s = res.settings;
      setEnabled(s.phantom_enabled === 'true');
      setPolishEnabled(s.phantom_polish_enabled === 'true');
      const t: Record<string, string> = {};
      for (const [k] of THRESHOLD_KEYS) t[k] = s[k] ?? '';
      setThresholds(t);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }, []);

  const loadHealth = useCallback(async (): Promise<void> => {
    try {
      const h = await phantomHealthcheck();
      setHealth(h);
      setHealthErr(null);
    } catch (e) {
      setHealthErr(e instanceof Error ? e.message : String(e));
    }
  }, []);

  const loadCurrent = useCallback(async (): Promise<void> => {
    if (!enabled) return;
    setReadBusy(true);
    try {
      const [json, md] = await Promise.all([
        phantomReadJson(),
        phantomReadMarkdown(),
      ]);
      setCurrentMd(md);
      const structured = json.structured as PhantomRegenSummary | null;
      if (structured && Array.isArray(structured.stubs)) {
        setCurrent(structured);
      } else {
        setCurrent(null);
      }
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setReadBusy(false);
    }
  }, [enabled]);

  const loadSuppressions = useCallback(async (): Promise<void> => {
    if (!enabled) return;
    try {
      const r = await phantomListSuppressions();
      setSuppressions(r.suppressions);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }, [enabled]);

  const loadHistory = useCallback(async (): Promise<void> => {
    if (!enabled) return;
    try {
      const r = await phantomHistory(20);
      setHistoryList(r.regenerations);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }, [enabled]);

  const loadHorizon = useCallback(async (): Promise<void> => {
    if (!enabled) return;
    try {
      const r = await phantomListHorizon();
      setOpDocs(r.operatorDocs);
      setBundledDocs(r.bundledDocs);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }, [enabled]);

  useEffect(() => { void loadSettings(); }, [loadSettings]);
  useEffect(() => { void loadHealth(); }, [loadHealth]);
  useEffect(() => {
    if (enabled) {
      void loadCurrent();
      void loadSuppressions();
      void loadHorizon();
    }
  }, [enabled, loadCurrent, loadSuppressions, loadHorizon]);

  const toggleEnabled = useCallback(async (): Promise<void> => {
    setErr(null); setInfo(null);
    const next = !(enabled ?? false);
    try {
      await apiJson('PUT', '/api/settings', { phantom_enabled: next ? 'true' : 'false' });
      setEnabled(next);
      onChanged();
      void loadHealth();
      setInfo(next ? 'Phantom enabled.' : 'Phantom disabled.');
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }, [enabled, onChanged, loadHealth]);

  const togglePolish = useCallback(async (): Promise<void> => {
    setErr(null); setInfo(null);
    const next = !polishEnabled;
    try {
      await apiJson('PUT', '/api/settings', { phantom_polish_enabled: next ? 'true' : 'false' });
      setPolishEnabled(next);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }, [polishEnabled]);

  const onRegenerate = useCallback(async (): Promise<void> => {
    setErr(null); setInfo(null);
    setRegenBusy(true);
    try {
      const summary = await phantomRegenerate();
      setCurrent(summary);
      // Re-fetch markdown so the viewer updates.
      const md = await phantomReadMarkdown();
      setCurrentMd(md);
      setInfo(`Regenerated: ${summary.stubCount} stubs in ${summary.durationMs}ms.`);
      void loadSuppressions();
      void loadHistory();
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e);
      setErr(msg);
    } finally {
      setRegenBusy(false);
    }
  }, [loadSuppressions, loadHistory]);

  const onSuppressSubmit = useCallback(async (): Promise<void> => {
    if (!suppressFor) return;
    setErr(null); setInfo(null);
    try {
      const expiresAt = suppressExpiry
        ? new Date(suppressExpiry).toISOString()
        : undefined;
      await phantomSuppress({
        stubId: suppressFor.id,
        reason: suppressReason.trim() || '(no reason given)',
        expiresAt,
      });
      setInfo(`Suppressed stub ${suppressFor.id.slice(0, 8)}…`);
      setSuppressFor(null);
      setSuppressReason('');
      setSuppressExpiry('');
      void loadCurrent();
      void loadSuppressions();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }, [suppressFor, suppressReason, suppressExpiry, loadCurrent, loadSuppressions]);

  const onUnsuppress = useCallback(async (stubId: string): Promise<void> => {
    setErr(null); setInfo(null);
    try {
      await phantomUnsuppress({ stubId });
      setInfo(`Unsuppressed stub ${stubId.slice(0, 8)}…`);
      void loadSuppressions();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }, [loadSuppressions]);

  const onCopy = useCallback(async (s: PhantomStub, rank: number): Promise<void> => {
    const text = stubToClipboardText(s, rank);
    const ok = await copyToClipboard(text);
    setInfo(ok ? `Copied stub #${rank} to clipboard.` : 'Clipboard unavailable.');
  }, []);

  const onThresholdChange = useCallback((key: string, value: string): void => {
    setThresholds((t) => ({ ...t, [key]: value }));
    if (debounceRefs.current[key]) clearTimeout(debounceRefs.current[key]!);
    debounceRefs.current[key] = setTimeout(() => {
      void apiJson('PUT', '/api/settings', { [key]: value }).catch((e) => {
        setErr(`Failed to save ${key}: ${e instanceof Error ? e.message : String(e)}`);
      });
    }, 600);
  }, []);

  const onUploadHorizon = useCallback(async (): Promise<void> => {
    if (!horizonFile) return;
    setErr(null); setInfo(null);
    try {
      const r = await phantomUploadHorizon(horizonFile.filename, horizonFile.body);
      setInfo(`Uploaded ${r.filename} (${r.bulletsParsed} bullets parsed).`);
      setHorizonFile(null);
      void loadHorizon();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }, [horizonFile, loadHorizon]);

  const onDeleteHorizon = useCallback(async (id: string): Promise<void> => {
    setErr(null); setInfo(null);
    try {
      await phantomDeleteHorizon(id);
      setInfo('Horizon doc deleted.');
      void loadHorizon();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    }
  }, [loadHorizon]);

  const onFilePicked = useCallback(async (file: File): Promise<void> => {
    if (!file.name.toLowerCase().endsWith('.md')) {
      setErr('Only .md files are accepted.');
      return;
    }
    const body = await file.text();
    setHorizonFile({ filename: file.name, body });
  }, []);

  const filteredStubs = useMemo(() => {
    if (!current) return [];
    return current.stubs.filter((s) => {
      if (filter !== 'all' && s.type !== filter) return false;
      if (s.valueScore < minScore) return false;
      return true;
    });
  }, [current, filter, minScore]);

  const lastRegenTs = current ? Math.floor(Date.parse(current.generatedAt) / 1000) : 0;

  // ─── render ────────────────────────────────────────────────────

  if (enabled === null) {
    return <div style={{ padding: 16 }}>Loading Phantom settings…</div>;
  }

  return (
    <div style={{ padding: 16, fontFamily: 'ui-sans-serif, system-ui', maxWidth: 880 }}>
      <h2 style={{ marginTop: 0 }}>Phantom — Growth-direction harness</h2>

      <div style={{ marginBottom: 12 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={enabled}
            onChange={toggleEnabled}
          />
          <span style={{ fontWeight: 600 }}>
            Enable Phantom (growth-direction harness)
          </span>
        </label>
        <p style={{ color: 'var(--muted)', fontSize: 13, margin: '6px 0 0 26px', lineHeight: 1.45 }}>
          Operator-only. Regenerates a ranked list of capability stubs from
          Memory + Homunculus residue. Never exposed to the chat model.
          The agent can <em>want</em>; only you can grow it.
        </p>
        {healthErr && (
          <p style={{ color: 'var(--danger)', fontSize: 12, margin: '6px 0 0 26px' }}>
            healthcheck: {healthErr}
          </p>
        )}
        {health && (
          <p style={{ color: 'var(--muted)', fontSize: 12, margin: '4px 0 0 26px' }}>
            worker {health.service} v{health.version} · region {health.region}
          </p>
        )}
      </div>

      {info && <div style={banner('rgba(110, 218, 159, 0.10)', 'var(--ok)')}>{info}</div>}
      {err && <div style={banner('rgba(255, 107, 107, 0.10)', 'var(--danger)')}>{err}</div>}

      {!enabled ? (
        <p style={{ color: 'var(--muted)', fontSize: 13 }}>
          Enable Phantom above to access the inspector, regenerate Phantom.md,
          manage suppressions, upload horizon docs, and tune detector thresholds.
        </p>
      ) : (
        <>
          {/* Last regeneration banner */}
          <section style={section()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
              <div>
                <strong>Last regeneration:</strong>{' '}
                {current
                  ? `${current.generatedAt} · ${current.stubCount} stubs · ${current.durationMs}ms · ${fmtAgo(lastRegenTs)}`
                  : 'never'}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={onRegenerate} disabled={regenBusy || readBusy}>
                  {regenBusy ? 'Regenerating…' : 'Regenerate now'}
                </button>
                <a
                  href={phantomExportUrl(API_BASE)}
                  target="_blank"
                  rel="noreferrer"
                  style={{ alignSelf: 'center' }}
                >
                  Export NDJSON
                </a>
              </div>
            </div>
            {current && (
              <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 6 }}>
                Inputs: memory{' '}
                {fmtBytes(current.inputs.memoryExportSize)} · homunculus{' '}
                {fmtBytes(current.inputs.homunculusExportSize)} · horizon{' '}
                {current.inputs.horizonDocsRead} doc(s) ·{' '}
                suppressed: {current.suppressedStubs}
              </div>
            )}
          </section>

          {/* Filter bar */}
          <section style={section()}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <span><strong>Filter:</strong></span>
              {(['all', 'capability-gap', 'redundancy', 'calibration-drift', 'architectural-pressure'] as TypeFilter[]).map((t) => (
                <label key={t} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13 }}>
                  <input
                    type="radio"
                    checked={filter === t}
                    onChange={() => setFilter(t)}
                  />
                  {t === 'all' ? 'all' : BADGE[t as StubType].label}
                </label>
              ))}
              <span style={{ marginLeft: 16 }}>
                <strong>Min score:</strong>{' '}
                <input
                  type="number"
                  min={0}
                  max={1}
                  step={0.05}
                  value={minScore}
                  onChange={(e) => setMinScore(Number(e.target.value))}
                  style={{ width: 70 }}
                />
              </span>
            </div>
          </section>

          {/* Stub list */}
          <section style={section()}>
            <h3 style={{ marginTop: 0 }}>Stubs ({filteredStubs.length})</h3>
            {filteredStubs.length === 0 && (
              <p style={{ color: 'var(--muted)', fontSize: 13 }}>
                {current
                  ? 'No stubs match the current filter.'
                  : 'No regeneration yet. Click "Regenerate now" to produce Phantom.md.'}
              </p>
            )}
            {filteredStubs.map((s, i) => {
              const rank = current ? current.stubs.indexOf(s) + 1 : i + 1;
              const isOpen = expandedStub === s.id;
              const badge = BADGE[s.type];
              return (
                <div
                  key={s.id}
                  style={{
                    border: '1px solid var(--border)',
                    borderRadius: 6,
                    padding: 10,
                    marginBottom: 8,
                    background: 'var(--panel)',
                    color: 'var(--fg)',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{
                      background: badge.bg, color: badge.fg,
                      padding: '2px 8px', borderRadius: 4, fontSize: 12, fontWeight: 600,
                    }}>{badge.label}</span>
                    <span style={{ fontFamily: 'ui-monospace, SFMono-Regular', fontSize: 12, color: 'var(--muted)' }}>
                      #{String(rank).padStart(3, '0')}
                    </span>
                    <span aria-label={`score zero point ${Math.round(s.valueScore * 100)}`}
                      style={{ fontFamily: 'ui-monospace, SFMono-Regular', fontWeight: 600 }}>
                      ✦ {fmtScore(s.valueScore)}
                    </span>
                    <span style={{ flex: 1, minWidth: 200, fontWeight: 500 }}>
                      {s.title}
                    </span>
                    <button onClick={() => onCopy(s, rank)}>Copy stub</button>
                    <button onClick={() => setSuppressFor(s)}>Suppress</button>
                    <button onClick={() => setExpandedStub(isOpen ? null : s.id)}>
                      {isOpen ? 'Hide' : 'Details'}
                    </button>
                  </div>
                  {isOpen && (
                    <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid var(--border)', fontSize: 13 }}>
                      <p><strong>Pressure:</strong> {s.pressure}</p>
                      <p><strong>Suggested scope:</strong> {s.suggestedScope}</p>
                      <p style={{ fontSize: 12, color: 'var(--muted)' }}>
                        First seen: {s.firstSeenAt} · Detected: {s.detectedAt}
                      </p>
                      <p style={{ fontFamily: 'ui-monospace, SFMono-Regular', fontSize: 11 }}>
                        Stub ID: {s.id}
                      </p>
                      <p style={{ fontSize: 12 }}>
                        <strong>Score breakdown:</strong>{' '}
                        freq {fmtScore(s.scoreBreakdown.frequency)} ·
                        recency {fmtScore(s.scoreBreakdown.recency)} ·
                        crossSrc {fmtScore(s.scoreBreakdown.crossSource)} ·
                        horizon {fmtScore(s.scoreBreakdown.horizonAlignment)} ·
                        cost {fmtScore(s.scoreBreakdown.cost)}
                      </p>
                      <p style={{ fontSize: 12 }}>
                        <strong>Evidence:</strong>
                        <ul style={{ margin: '4px 0', paddingLeft: 20 }}>
                          {s.evidence.map((e, ei) => (
                            <li key={ei}>
                              {e.source} · {e.detector} ×{e.signalCount} —{' '}
                              <em>{e.excerpt.slice(0, 100)}{e.excerpt.length > 100 ? '…' : ''}</em>
                              {e.references.length > 0 && (
                                <span style={{ color: 'var(--muted)' }}>
                                  {' '}refs: {e.references.slice(0, 3).join(', ')}
                                  {e.references.length > 3 ? '…' : ''}
                                </span>
                              )}
                            </li>
                          ))}
                        </ul>
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </section>

          {/* Markdown preview (collapsible) */}
          <details style={section()}>
            <summary style={{ cursor: 'pointer', fontWeight: 600 }}>
              Phantom.md (raw markdown preview)
            </summary>
            <pre style={{
              whiteSpace: 'pre-wrap',
              background: 'var(--bg)',
              border: '1px solid var(--border)',
              color: 'var(--fg)',
              padding: 8,
              borderRadius: 4,
              fontSize: 12,
              maxHeight: 400,
              overflow: 'auto',
            }}>{currentMd || '(empty)'}</pre>
          </details>

          {/* Suppressions */}
          <section style={section()}>
            <button
              onClick={() => setSuppressionsOpen((o) => !o)}
              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontWeight: 600, fontSize: 14 }}
            >
              {suppressionsOpen ? '▾' : '▸'} Suppressions ({suppressions.length} active)
            </button>
            {suppressionsOpen && (
              <div style={{ marginTop: 8 }}>
                {suppressions.length === 0 && (
                  <p style={{ fontSize: 13, color: 'var(--muted)' }}>No active suppressions.</p>
                )}
                {suppressions.map((sp) => (
                  <div key={sp.stubId} style={{ borderTop: '1px solid var(--border-soft)', padding: '6px 0', fontSize: 13 }}>
                    <div style={{ fontFamily: 'ui-monospace, SFMono-Regular', fontSize: 11, color: 'var(--muted)' }}>
                      {sp.stubId}
                    </div>
                    <div>{sp.reason}</div>
                    <div style={{ fontSize: 11, color: 'var(--muted)' }}>
                      Suppressed {sp.suppressedAt}
                      {sp.expiresAt ? ` · expires ${sp.expiresAt}` : ' · never expires'}
                    </div>
                    <button onClick={() => onUnsuppress(sp.stubId)} style={{ marginTop: 4 }}>
                      Restore
                    </button>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* History */}
          <section style={section()}>
            <button
              onClick={() => {
                const next = !historyOpen;
                setHistoryOpen(next);
                if (next) void loadHistory();
              }}
              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontWeight: 600, fontSize: 14 }}
            >
              {historyOpen ? '▾' : '▸'} History
            </button>
            {historyOpen && (
              <div style={{ marginTop: 8 }}>
                {history.length === 0 && (
                  <p style={{ fontSize: 13, color: 'var(--muted)' }}>
                    No prior regenerations.
                  </p>
                )}
                {history.map((h) => (
                  <div key={h.regenerationId} style={{ borderTop: '1px solid var(--border-soft)', padding: '6px 0', fontSize: 12, fontFamily: 'ui-monospace, SFMono-Regular' }}>
                    {h.generatedAt} · {h.stubCount} stubs · {h.durationMs}ms ·{' '}
                    <span style={{ color: 'var(--muted)' }}>{h.regenerationId.slice(0, 8)}…</span>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Horizon docs */}
          <section style={section()}>
            <button
              onClick={() => setHorizonOpen((o) => !o)}
              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontWeight: 600, fontSize: 14 }}
            >
              {horizonOpen ? '▾' : '▸'} Horizon docs ({bundledDocs.length} bundled, {opDocs.length} operator)
            </button>
            {horizonOpen && (
              <div style={{ marginTop: 8, fontSize: 13 }}>
                <div style={{ marginBottom: 8 }}>
                  <strong>Bundled (read-only):</strong>
                  <ul style={{ margin: '4px 0', paddingLeft: 20 }}>
                    {bundledDocs.length === 0 && <li style={{ color: 'var(--muted)' }}>(none)</li>}
                    {bundledDocs.map((d) => (
                      <li key={d.filename}>
                        {d.filename} <span style={{ color: 'var(--muted)' }}>({fmtBytes(d.bodySize)})</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div style={{ marginBottom: 8 }}>
                  <strong>Operator-uploaded:</strong>
                  <ul style={{ margin: '4px 0', paddingLeft: 20 }}>
                    {opDocs.length === 0 && <li style={{ color: 'var(--muted)' }}>(none)</li>}
                    {opDocs.map((d) => (
                      <li key={d.id} style={{ marginBottom: 4 }}>
                        {d.filename} <span style={{ color: 'var(--muted)' }}>({fmtBytes(d.bodySize)} · {d.uploadedAt})</span>{' '}
                        <button onClick={() => onDeleteHorizon(d.id)} style={{ marginLeft: 6 }}>Delete</button>
                      </li>
                    ))}
                  </ul>
                </div>
                <div style={{ marginTop: 8 }}>
                  <input
                    type="file"
                    accept=".md,text/markdown"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) void onFilePicked(f);
                    }}
                  />
                  {horizonFile && (
                    <div style={{ marginTop: 6 }}>
                      <span style={{ fontSize: 12 }}>
                        Ready: <strong>{horizonFile.filename}</strong> ({horizonFile.body.length} chars)
                      </span>
                      <button onClick={onUploadHorizon} style={{ marginLeft: 8 }}>Upload</button>
                      <button onClick={() => setHorizonFile(null)} style={{ marginLeft: 4 }}>Cancel</button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </section>

          {/* Detector thresholds */}
          <section style={section()}>
            <button
              onClick={() => setThresholdsOpen((o) => !o)}
              style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontWeight: 600, fontSize: 14 }}
            >
              {thresholdsOpen ? '▾' : '▸'} Detector thresholds
            </button>
            {thresholdsOpen && (
              <div style={{ marginTop: 8, fontSize: 13 }}>
                <p style={{ color: 'var(--muted)', fontSize: 12 }}>
                  Tune per profile. Saves debounced 600ms after the last keystroke.
                </p>
                <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                  <tbody>
                    {THRESHOLD_KEYS.map(([key, label, dflt]) => (
                      <tr key={key}>
                        <td style={{ padding: '4px 6px', fontFamily: 'ui-monospace, SFMono-Regular', fontSize: 11, color: 'var(--muted)' }}>
                          {key}
                        </td>
                        <td style={{ padding: '4px 6px' }}>{label}</td>
                        <td style={{ padding: '4px 6px' }}>
                          <input
                            type="text"
                            value={thresholds[key] ?? dflt}
                            onChange={(e) => onThresholdChange(key, e.target.value)}
                            style={{ width: 80 }}
                          />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          {/* Polish toggle */}
          <section style={section()}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input type="checkbox" checked={polishEnabled} onChange={togglePolish} />
              <span>Use Gemini to polish stub prose</span>
            </label>
            <p style={{ color: 'var(--muted)', fontSize: 12, margin: '4px 0 0 26px' }}>
              Optional readability uplift only. Deterministic phrasing remains the
              source of truth — the polish output is silently rejected if it drops
              or changes any signature-payload tokens or signal counts.
            </p>
          </section>
        </>
      )}

      {/* Suppress modal */}
      {suppressFor && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0, 0, 0, 0.55)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
        }} onClick={() => setSuppressFor(null)}>
          <div style={{
            background: 'var(--panel)', color: 'var(--fg)',
            border: '1px solid var(--border)',
            padding: 20, borderRadius: 8, maxWidth: 480,
            width: '90%', boxShadow: '0 10px 25px rgba(0,0,0,0.4)',
          }} onClick={(e) => e.stopPropagation()}>
            <h3 style={{ marginTop: 0 }}>Suppress stub</h3>
            <p style={{ fontSize: 13, color: 'var(--muted)' }}>
              {suppressFor.title}
            </p>
            <p style={{ fontFamily: 'ui-monospace, SFMono-Regular', fontSize: 11, color: 'var(--muted)' }}>
              {suppressFor.id}
            </p>
            <label style={{ display: 'block', marginTop: 12, fontSize: 13 }}>
              Reason (≤ 500 chars):
              <textarea
                value={suppressReason}
                onChange={(e) => setSuppressReason(e.target.value.slice(0, 500))}
                rows={3}
                style={{ width: '100%', boxSizing: 'border-box', marginTop: 4 }}
                placeholder="Why are you suppressing this stub?"
              />
            </label>
            <label style={{ display: 'block', marginTop: 8, fontSize: 13 }}>
              Expires (optional, future):
              <input
                type="datetime-local"
                value={suppressExpiry}
                onChange={(e) => setSuppressExpiry(e.target.value)}
                style={{ marginLeft: 8 }}
              />
            </label>
            <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button onClick={() => setSuppressFor(null)}>Cancel</button>
              <button
                onClick={onSuppressSubmit}
                disabled={!suppressReason.trim() || (
                  !!suppressExpiry && Date.parse(suppressExpiry) <= Date.now()
                )}
              >
                Suppress
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function section(): React.CSSProperties {
  return {
    marginTop: 16,
    padding: 12,
    background: 'var(--bg-soft)',
    borderRadius: 6,
    border: '1px solid var(--border-soft)',
    color: 'var(--fg)',
  };
}

function banner(bg: string, fg: string): React.CSSProperties {
  return {
    margin: '8px 0',
    padding: '8px 12px',
    background: bg,
    color: fg,
    border: '1px solid var(--border-soft)',
    borderRadius: 4,
    fontSize: 13,
  };
}

function fmtBytes(b: number | null): string {
  if (b === null) return 'n/a';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

export default PhantomTab;
